import React, { useEffect, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const STATUS_TONE = {
  active: "text-emerald-600",
  auto_expired_over_cap: "text-muted-foreground",
  expired: "text-muted-foreground",
  paused: "text-amber-600",
};

export default function FeaturedTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [extendFor, setExtendFor] = useState(null);
  const [days, setDays] = useState("14");

  const load = () => {
    setLoading(true);
    api.get("/admin/featured")
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const call = async (fn, ok) => {
    try { await fn(); toast.success(ok); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const pause  = (id) => call(() => api.post(`/admin/featured/${id}/pause`), "Paused");
  const resume = (id) => call(() => api.post(`/admin/featured/${id}/resume`), "Resumed");
  const cancel = (id) => {
    if (!window.confirm("Cancel this featured slot immediately?")) return;
    call(() => api.delete(`/marketplace/featured/${id}`), "Cancelled");
  };
  const extend = () => {
    const n = Number(days);
    if (!n || n < 1) { toast.error("Enter days ≥ 1"); return; }
    call(async () => {
      await api.post(`/admin/featured/${extendFor.featured_id}/extend`, { days: n });
      setExtendFor(null);
    }, `Extended by ${n} days`);
  };

  const active = items.filter((x) => x.status === "active" && !x.paused);
  const paused = items.filter((x) => x.paused);
  const others = items.filter((x) => x.status !== "active");

  return (
    <div className="space-y-6" data-testid="admin-featured-tab">
      <div className="border border-border p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="font-overline text-accent">Rail Composition</div>
          <div className="text-[11px] font-mono text-muted-foreground">
            {active.length} / 20 active · {paused.length} paused · Newest at position #1
          </div>
        </div>
        <Button variant="outline" className="rounded-none uppercase text-xs h-9" onClick={load} data-testid="featured-refresh">Refresh</Button>
      </div>

      <Section title={`Active (${active.length})`} testid="featured-active-list">
        <Table items={active} onPause={pause} onResume={resume} onExtend={setExtendFor} onCancel={cancel} />
      </Section>
      {paused.length > 0 && (
        <Section title={`Paused (${paused.length})`} testid="featured-paused-list">
          <Table items={paused} onPause={pause} onResume={resume} onExtend={setExtendFor} onCancel={cancel} />
        </Section>
      )}
      <Section title={`Expired / Removed (${others.length})`} testid="featured-expired-list">
        <Table items={others} readOnly />
      </Section>

      {loading && <div className="text-muted-foreground p-4 text-center text-sm">Loading…</div>}

      {extendFor && (
        <Dialog open onOpenChange={() => setExtendFor(null)}>
          <DialogContent className="rounded-none max-w-md" data-testid="featured-extend-dialog">
            <DialogHeader><DialogTitle>Extend Featured Slot</DialogTitle></DialogHeader>
            <div className="space-y-2 text-sm">
              <div className="text-xs text-muted-foreground">Current expires: {extendFor.expires_at?.slice(0, 16).replace("T", " ")}</div>
              <div>Days to extend</div>
              <Input type="number" min="1" value={days} onChange={(e) => setDays(e.target.value)} className="rounded-none" data-testid="featured-extend-days" />
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-none" onClick={() => setExtendFor(null)}>Cancel</Button>
              <Button className="rounded-none" onClick={extend} data-testid="featured-extend-confirm">Extend</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

const Section = ({ title, testid, children }) => (
  <div className="space-y-2">
    <div className="font-overline text-accent">{title}</div>
    <div className="border border-border overflow-x-auto" data-testid={testid}>
      {children}
    </div>
  </div>
);

function Table({ items, onPause, onResume, onExtend, onCancel, readOnly }) {
  if (!items || items.length === 0) return <div className="p-4 text-center text-muted-foreground text-sm">Nothing here.</div>;
  return (
    <table className="w-full text-sm min-w-[900px]">
      <thead className="bg-muted/50 text-left">
        <tr>
          <Th>Position</Th><Th>Note</Th><Th>Start</Th><Th>Expires</Th><Th>Payment</Th><Th>Status</Th><Th>Actions</Th>
        </tr>
      </thead>
      <tbody>
        {items.map((r) => (
          <tr key={r.featured_id} className="border-t border-border" data-testid={`featured-row-${r.featured_id}`}>
            <Td className="font-mono text-[12px]">#{r.position ?? "—"}</Td>
            <Td>
              {r.note_id && (
                <Link to={`/notes/${r.note_id}`} className="text-primary hover:underline text-[12px]">
                  {r.note_id.slice(-10)}
                </Link>
              )}
              <div className="text-[10px] text-muted-foreground font-mono">{r.featured_id.slice(-10)}</div>
            </Td>
            <Td className="text-[10px] font-mono">{r.starts_at?.slice(0, 10) || "—"}</Td>
            <Td className="text-[10px] font-mono">{r.expires_at?.slice(0, 10) || "—"}</Td>
            <Td className="text-[10px] font-mono uppercase">
              {r.payment_status || "—"}
              {r.price_paid ? <div className="text-muted-foreground">{fmtMoney(r.price_paid)}</div> : null}
            </Td>
            <Td className={`text-[10px] font-mono uppercase ${STATUS_TONE[r.paused ? "paused" : r.status] || ""}`}>
              {r.paused ? "paused" : r.status || "—"}
            </Td>
            <Td>
              {!readOnly && (
                <div className="flex gap-1 flex-wrap">
                  {r.paused ? (
                    <Btn onClick={() => onResume(r.featured_id)} testid={`featured-resume-${r.featured_id}`}>Resume</Btn>
                  ) : (
                    <Btn onClick={() => onPause(r.featured_id)} testid={`featured-pause-${r.featured_id}`}>Pause</Btn>
                  )}
                  <Btn onClick={() => onExtend(r)} testid={`featured-extend-${r.featured_id}`}>Extend</Btn>
                  <Btn onClick={() => onCancel(r.featured_id)} tone="danger" testid={`featured-cancel-${r.featured_id}`}>Cancel</Btn>
                </div>
              )}
            </Td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const Th = ({ children }) => <th className="px-3 py-2 font-overline text-muted-foreground text-[10px] whitespace-nowrap">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-3 py-2 ${className}`}>{children}</td>;
const Btn = ({ children, onClick, testid, tone }) => (
  <Button size="sm" variant="outline" className={`rounded-none text-[10px] h-7 uppercase ${tone === "danger" ? "text-red-600" : ""}`} onClick={onClick} data-testid={testid}>{children}</Button>
);
